import { useCallback, useEffect, useReducer, useRef } from "react";
import { DialogContext } from "./DialogContext";
import { AlertDialog, ConfirmDialog } from "./Dialog";

type DialogContextProviderProps = {
  children: React.ReactNode;
};

type DialogState =
  | { type: "closed" }
  | {
      type: "alert";
      title: string;
      message: string;
    }
  | {
      type: "confirm";
      title: string;
      message: string;
      confirmLabel?: string;
    };

type DialogAction =
  | { type: "SHOW_ALERT"; title: string; message: string }
  | {
      type: "SHOW_CONFIRM";
      title: string;
      message: string;
      confirmLabel?: string;
    }
  | { type: "CLOSE" };

function dialogReducer(state: DialogState, action: DialogAction): DialogState {
  switch (action.type) {
    case "SHOW_ALERT":
      return {
        type: "alert",
        title: action.title,
        message: action.message,
      };
    case "SHOW_CONFIRM":
      return {
        type: "confirm",
        title: action.title,
        message: action.message,
        confirmLabel: action.confirmLabel,
      };
    case "CLOSE":
      return { type: "closed" };
    default:
      return state;
  }
}

export const DialogContextProvider = ({
  children,
}: DialogContextProviderProps) => {
  const [state, dispatch] = useReducer(dialogReducer, { type: "closed" });
  const pendingResolverRef = useRef<null | ((value: boolean) => void)>(null);

  // Ensure any still-pending confirm promise is resolved on unmount
  useEffect(() => {
    return () => {
      if (pendingResolverRef.current) {
        try {
          pendingResolverRef.current(false);
        } finally {
          pendingResolverRef.current = null;
        }
      }
    };
  }, []);

  const alert = useCallback(
    ({ title, message }: { title?: string; message: string }) => {
      // If a confirm was pending, cancel it before showing alert
      if (pendingResolverRef.current) {
        try {
          pendingResolverRef.current(false);
        } finally {
          pendingResolverRef.current = null;
        }
      }
      const normalizedTitle = title ?? "";
      dispatch({ type: "SHOW_ALERT", title: normalizedTitle, message });
    },
    [],
  );

  const confirm = useCallback(
    ({
      title,
      message,
      confirmLabel,
    }: {
      title: string;
      message: string;
      confirmLabel?: string;
    }): Promise<boolean> => {
      // If another confirm is pending, cancel it before opening a new one
      if (pendingResolverRef.current) {
        try {
          pendingResolverRef.current(false);
        } finally {
          pendingResolverRef.current = null;
        }
      }

      dispatch({ type: "SHOW_CONFIRM", title, message, confirmLabel });

      return new Promise<boolean>((resolve) => {
        let hasResolved = false;
        pendingResolverRef.current = (value: boolean) => {
          if (hasResolved) {
            return;
          }
          hasResolved = true;
          try {
            resolve(value);
          } finally {
            pendingResolverRef.current = null;
          }
        };
      });
    },
    [],
  );

  const finish = useCallback((value: boolean) => {
    // Resolve current confirm promise (if any), then close dialog
    const resolver = pendingResolverRef.current;
    if (resolver) {
      try {
        resolver(value);
      } finally {
        pendingResolverRef.current = null;
      }
    }
    dispatch({ type: "CLOSE" });
  }, []);

  const handleClose = useCallback(() => {
    // If closing a confirm, treat as cancel. If alert, just close.
    if (state.type === "confirm") {
      finish(false);
    } else {
      dispatch({ type: "CLOSE" });
    }
  }, [state.type, finish]);

  const handleConfirmTrue = useCallback(() => finish(true), [finish]);
  const handleConfirmFalse = useCallback(() => finish(false), [finish]);

  return (
    <DialogContext.Provider value={{ alert, confirm }}>
      {children}

      {/* Only render the appropriate dialog based on state */}
      {state.type === "alert" && (
        <AlertDialog
          isOpen
          title={state.title}
          message={state.message}
          onConfirm={handleClose}
          onClose={handleClose}
        />
      )}

      {state.type === "confirm" && (
        <ConfirmDialog
          isOpen
          title={state.title}
          message={state.message}
          confirmLabel={state.confirmLabel}
          onConfirm={handleConfirmTrue}
          onCancel={handleConfirmFalse}
          onClose={handleClose}
        />
      )}
    </DialogContext.Provider>
  );
};
